import { useMemo } from 'react';

import { EwusPatient } from '@dreryk/egabinet-ewus-frontend-api';

import { Translate } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   DataConfiguration,
   Modal,
   ModalWidth,
   StrongText,
   StyledFlex,
   Table,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { downloadPersonalDataFromEwusTranslationKeys } from '../../../translations';

import { PatientComparisonTableBean } from './EwusPatientDownload.types';

type EwusPatientComparisonModalProps = {
   readonly patient: {
      firstName: string | null;
      lastName: string | null;
   };
   readonly ewusPatient: EwusPatient;
   readonly children: JSX.Element;
};

const usePatientComparisonTableColumns = (): TableColumnConfiguration<PatientComparisonTableBean>[] => {
   const columnsWidth = createDynamicColumnsWidth({
      type: '90px',
      file: '50%',
      ewus: '50%',
   });

   return [
      {
         accessor: 'type',
         label: downloadPersonalDataFromEwusTranslationKeys.type,
         width: columnsWidth.type,

         renderer: (props: ValueRendererProps<PatientComparisonTableBean>) => {
            return (
               <StrongText testId={props.bean.type}>
                  <Translate>
                     {props.bean.type === 'firstName'
                        ? downloadPersonalDataFromEwusTranslationKeys.firstName
                        : downloadPersonalDataFromEwusTranslationKeys.lastName}
                  </Translate>
               </StrongText>
            );
         },
         sortingDisabled: true,
      },
      {
         accessor: 'patient',
         label: downloadPersonalDataFromEwusTranslationKeys.file,
         width: columnsWidth.file,

         renderer: (props: ValueRendererProps<PatientComparisonTableBean>) => {
            return <>{props.bean.type === 'firstName' ? props.bean.patient.firstName : props.bean.patient.lastName}</>;
         },
         sortingDisabled: true,
      },
      {
         accessor: 'ewusPatient',
         label: downloadPersonalDataFromEwusTranslationKeys.ewus,
         width: columnsWidth.ewus,

         renderer: (props: ValueRendererProps<PatientComparisonTableBean>) => {
            return (
               <>
                  {props.bean.type === 'firstName'
                     ? props.bean.ewusPatient.firstName?.asString()
                     : props.bean.ewusPatient.lastName?.asString()}
               </>
            );
         },
         sortingDisabled: true,
      },
   ];
};

export const EwusPatientComparisonModal = (props: EwusPatientComparisonModalProps): JSX.Element => {
   const comparisonTableColumnConfiguration: DataConfiguration<PatientComparisonTableBean> = useMemo(() => {
      return {
         data: [
            { type: 'firstName', patient: props.patient, ewusPatient: props.ewusPatient },
            { type: 'lastName', patient: props.patient, ewusPatient: props.ewusPatient },
         ],
      };
   }, [props.ewusPatient, props.patient]);

   return (
      <Modal
         testId={'ewusPatientReplacementModal'}
         title={downloadPersonalDataFromEwusTranslationKeys.ewusData}
         width={ModalWidth.NORMAL}
      >
         <StyledFlex gap={'18px'} direction={'column'}>
            <Translate>{downloadPersonalDataFromEwusTranslationKeys.ewusDataIsDifferent}</Translate>
            <Table<PatientComparisonTableBean>
               testId={'patientDataComparision'}
               columns={usePatientComparisonTableColumns()}
               dataConfiguration={comparisonTableColumnConfiguration}
               keyExtractor={'type'}
               compact
            />
         </StyledFlex>
         {props.children}
      </Modal>
   );
};
